import React from 'react';
import { useTranslation } from 'react-i18next';
import { MemberDetails } from '~/models/MemberDetails';
import { useCommunityTranslations } from '~/hooks/useCommunityTranslations';
import { useIsLargeScreen } from '~/hooks/useIsLargeScreen';
import { Box } from '~/components/gluestack/box';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { RenderHtml } from '~/components/ui/RenderHtml';
import { ProfileItem } from './ProfileItem';
import { PresenceIndicator } from '~/components/domain/members/PresenceIndicator';
import { RelativeTime } from '~/components/ui/RelativeTime';
import { ProfileSection } from './ProfileSection';
import { ProfilePhotosPreview } from './ProfilePhotosPreview';
import { PROFILE_PHOTOS_PREVIEW_MAX_COUNT } from '@/hooks/useMemberPreviewPhotos';
import { useCommunity } from '~/hooks/useCommunity';
import { useMeasurementFormatters } from '~/components/providers/MeasurementsProvider';
import { Badge, BadgeText } from '~/components/gluestack/badge';
import { ButtonWrapper } from '~/components/ui/ButtonWrapper';
import {
  WeightScaleIcon,
  RulerIcon,
  FavouriteIcon,
  LanguageSkillIcon,
  Calendar03Icon,
  EarthIcon,
  LocationUser02Icon,
  Target01Icon,
  TapeMeasureIcon,
  WorkoutRunIcon,
  Fire02Icon,
  PathfinderIntersectIcon,
  Clock01Icon,
  ViewIcon,
  DashboardSpeed02Icon,
  Chart03Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import dayjs from '@/i18n/dayjs-config';

type DomainSection = string;

interface ProfileTabAboutProps {
  member: MemberDetails;
  onWeightChartPress?: () => void;
  onPhotosTabPress?: () => void;
}

export function ProfileTabAbout({
  member,
  onWeightChartPress,
  onPhotosTabPress,
}: ProfileTabAboutProps) {
  const { t } = useTranslation(['default', 'domain']);
  const { getCommunityTranslation } = useCommunityTranslations();
  const isLargeScreen = useIsLargeScreen();
  const community = useCommunity();
  const { formatHeight, formatWeight, formatBmi, hasAdvancedWeightFeatures } =
    useMeasurementFormatters();

  // Helper function to render badges for arrays with domain translations
  const renderBadges = (values: string[], section?: DomainSection) => {
    if (!values || values.length === 0) return null;

    return (
      <HStack className="flex-wrap gap-2 md:mt-1">
        {values.map((value, index) => {
          const displayValue = section ? t(`domain:${section}.${value}`, value) : value;
          return (
            <Badge
              key={index}
              size="md"
              className="rounded-xl bg-background-500 dark:bg-background-200">
              <BadgeText className="font-semibold text-white">{displayValue}</BadgeText>
            </Badge>
          );
        })}
      </HStack>
    );
  };

  // Helper function to format a single item with domain translation
  const formatDomainItem = (value: string | undefined, section: DomainSection): string => {
    if (!value) return '';
    return t(`domain:${section}.${value}`, value);
  };

  const formatJoinDate = (joinDate: string | Date): string => {
    return dayjs(joinDate).format('MMM YYYY');
  };

  // Get community name for join date label
  const communityName = community?.name || '';
  const joinDateLabel = t('profile.joinDateWithCommunity', { communityName });

  // Character limit for descriptions - 3x more on large screens
  const characterLimit = isLargeScreen ? 1800 : 600;

  return (
    <VStack className="gap-3 px-3 md:px-4">
      <ProfilePhotosPreview
        memberId={member.Id}
        publicPhotosCount={member.PublicPhotosCount ?? 0}
        profilePhotoId={member.PhotoId}
        profilePhotoUrl={member.PhotoUrl}
        maxCount={PROFILE_PHOTOS_PREVIEW_MAX_COUNT}
        onSeeAllPress={onPhotosTabPress}
        showLoadingSkeleton={(member.PublicPhotosCount ?? 0) >= 2}
        skeletonCount={(member.PublicPhotosCount ?? 1) - 1}
        size={isLargeScreen ? 'lg' : 'sm'}
      />

      {member.Description && (
        <ProfileSection className="pt-2 md:pt-1">
          <VStack className="gap-0">
            <RenderHtml
              html={member.Description}
              characterLimit={characterLimit}
              detectHttpsLinks
              detectHashtags
              noTruncate={member.IsAdmin}
            />
          </VStack>
        </ProfileSection>
      )}

      <ProfileSection title={t('profile.stats', 'Stats')} noCard={!isLargeScreen}>
        {/* Stats */}
        {(member.HeightCm != null || member.WeightKg != null) && (
          <HStack className="gap-6 md:gap-8">
            {member.HeightCm != null && (
              <ProfileItem
                icon={RulerIcon}
                label={t('profile.height', 'Height')}
                labelPosition={isLargeScreen ? 'above' : 'hidden'}
                compact>
                {formatHeight(member.HeightCm)}
              </ProfileItem>
            )}
            {member.WeightKg != null && (
              <HStack className="gap-2 self-start">
                <ProfileItem
                  icon={WeightScaleIcon}
                  label={t('profile.weight', 'Weight')}
                  labelPosition={isLargeScreen ? 'above' : 'hidden'}
                  compact>
                  {formatWeight(member.WeightKg, member.WeightGoalKg)}
                </ProfileItem>
                {onWeightChartPress && member.HasWeightRecords && (
                  <Box className="relative w-8 self-stretch">
                    <Box className="absolute left-0 top-1/2 -translate-y-1/2">
                      <ButtonWrapper
                        icon={Chart03Icon}
                        variant="solid"
                        size="xs"
                        onPress={onWeightChartPress}
                      />
                    </Box>
                  </Box>
                )}
              </HStack>
            )}
            {hasAdvancedWeightFeatures &&
              member.WeightKg != null &&
              member.HeightCm != null &&
              formatBmi(member.WeightKg, member.HeightCm) !== '' && (
                <ProfileItem
                  icon={DashboardSpeed02Icon}
                  label={t('weightHistory.bmi', 'BMI')}
                  labelPosition={isLargeScreen ? 'above' : 'hidden'}
                  compact>
                  {!isLargeScreen && `${t('weightHistory.bmi', 'BMI')} `}
                  {formatBmi(member.WeightKg, member.HeightCm)}
                </ProfileItem>
              )}
          </HStack>
        )}
        {member.Gender && member.Gender.length > 0 && (
          <ProfileItem
            icon={LocationUser02Icon}
            label={t('profile.gender', 'Gender')}
            labelPosition={isLargeScreen ? 'above' : 'hidden'}>
            {renderBadges(member.Gender, 'ProfileTags.Other')}
          </ProfileItem>
        )}

        {member.Relationship && member.Relationship.length > 0 && (
          <ProfileItem
            icon={FavouriteIcon}
            label={t('profile.relationship', 'Relationship')}
            labelPosition={isLargeScreen ? 'above' : 'hidden'}>
            {renderBadges(member.Relationship, 'ProfileTags.Other')}
          </ProfileItem>
        )}
        {member.Languages && member.Languages.length > 0 && (
          <ProfileItem
            icon={LanguageSkillIcon}
            label={t('profile.languages', 'Languages')}
            labelPosition={isLargeScreen ? 'above' : 'hidden'}>
            {renderBadges(member.Languages, 'ProfileTags.Languages')}
          </ProfileItem>
        )}
        {member.Ethnicity && (
          <ProfileItem
            icon={EarthIcon}
            label={t('profile.origin', 'Origin')}
            labelPosition={isLargeScreen ? 'above' : 'hidden'}>
            {renderBadges([member.Ethnicity], 'ProfileTags.Ethnicities')}
          </ProfileItem>
        )}

        {member.JoinDate && (
          <ProfileItem icon={Calendar03Icon} label={joinDateLabel} labelPosition="left">
            {formatJoinDate(member.JoinDate)}
          </ProfileItem>
        )}
      </ProfileSection>

      {/* Looking For Section */}
      {!member.IsAdmin &&
        (member.InterestedIn?.length ||
          member.LookingFor?.length ||
          member.BodyTypesLiked?.length ||
          member.BodyPartsLiked?.length ||
          member.SexualInterests?.length ||
          member.MutualKinks?.length) && (
          <ProfileSection title={t('profile.preferences', 'Preferences')}>
            {member.InterestedIn && member.InterestedIn.length > 0 && (
              <ProfileItem
                icon={LocationUser02Icon}
                label={t('profile.interestedIn', 'Interested In')}>
                {renderBadges(member.InterestedIn, 'ProfileTags.Other')}
              </ProfileItem>
            )}
            {member.LookingFor && member.LookingFor.length > 0 && (
              <ProfileItem icon={Target01Icon} label={t('profile.lookingFor', 'Looking For')}>
                {renderBadges(member.LookingFor, 'ProfileTags.Interests')}
              </ProfileItem>
            )}
            {member.BodyTypesLiked && member.BodyTypesLiked.length > 0 && (
              <ProfileItem icon={TapeMeasureIcon} label={t('profile.bodyTypesLiked', 'Body Types')}>
                {renderBadges(member.BodyTypesLiked, 'ProfileTags.BodyFeatures')}
              </ProfileItem>
            )}
            {member.BodyPartsLiked && member.BodyPartsLiked.length > 0 && (
              <ProfileItem
                icon={WorkoutRunIcon}
                label={t('profile.bodyPartsLiked', 'Body Features')}>
                {renderBadges(member.BodyPartsLiked, 'ProfileTags.BodyFeatures')}
              </ProfileItem>
            )}
            {member.SexualInterests && member.SexualInterests.length > 0 && (
              <ProfileItem
                icon={Fire02Icon}
                label={t('profile.sexualInterests', 'Sexual Interests')}>
                {renderBadges(member.SexualInterests, 'ProfileTags.Other')}
              </ProfileItem>
            )}
            {member.MutualKinks && member.MutualKinks.length > 0 && (
              <ProfileItem
                icon={PathfinderIntersectIcon}
                label={t('profile.mutualKinks', 'Mutual Kinks')}>
                {renderBadges(member.MutualKinks, 'ProfileTags.Other')}
              </ProfileItem>
            )}
          </ProfileSection>
        )}

      {/* Custom Text Fields */}
      {!member.IsAdmin && member.TextField1 && (
        <ProfileSection title={getCommunityTranslation('TextField1')}>
          <VStack className="gap-0">
            <RenderHtml
              html={member.TextField1}
              characterLimit={characterLimit}
              detectHttpsLinks={true}
              detectHashtags
            />
          </VStack>
        </ProfileSection>
      )}
      {!member.IsAdmin && member.TextField2 && (
        <ProfileSection title={getCommunityTranslation('TextField2')}>
          <VStack className="gap-0">
            <RenderHtml
              html={member.TextField2}
              characterLimit={characterLimit}
              detectHttpsLinks={true}
              detectHashtags
            />
          </VStack>
        </ProfileSection>
      )}
      {!member.IsAdmin && member.TextField3 && (
        <ProfileSection title={getCommunityTranslation('TextField3')}>
          <VStack className="gap-0">
            <RenderHtml
              html={member.TextField3}
              characterLimit={characterLimit}
              detectHttpsLinks={true}
              detectHashtags
            />
          </VStack>
        </ProfileSection>
      )}

      {/* Date Fields Section - Moved to end */}
      {!member.IsAdmin && (
        <ProfileSection title={t('profile.activity', 'Activity')} noCard={!isLargeScreen}>
          {member.LastSeenDate && (
            <ProfileItem
              icon={Clock01Icon}
              label={t('profile.lastSeen', 'Last seen')}
              labelPosition="left">
              <HStack className="items-center gap-1.5">
                <PresenceIndicator member={member} />
                <RelativeTime date={member.LastSeenDate} className="font-medium" />
              </HStack>
            </ProfileItem>
          )}
          <ProfileItem
            icon={ViewIcon}
            label={t('profile.lastVisitedYou', 'Last Visited You')}
            labelPosition="left">
            {member.LastVisitedYouDate ? (
              <RelativeTime date={member.LastVisitedYouDate} textOnly className="font-medium" />
            ) : (
              t('profile.notYet', 'Not yet')
            )}
          </ProfileItem>
        </ProfileSection>
      )}
    </VStack>
  );
}
